import { FC, useMemo } from 'react';

import { Ad } from '@adflow/ad';
import { AdElement } from '@adflow/types';
import type { ElementAnimationStyle } from '@adflow/utils';
import { Box } from '@chakra-ui/react';

import PreviewOverlay from '../PreviewOverlay';
import SelectOverlay from '../SelectOverlay';
import { PREVIEW_MAX_SIZE, getPreviewScaledSize } from '../previewConstants';

export type PreviewPresentationProps = {
  layoutElements: AdElement[];
  mappedElements: AdElement[];
  animationOverrides: ReadonlyMap<string, ElementAnimationStyle> | null;
  selectedElementId?: string | null;
  isIndexPreview: boolean;
  forceTopLeftOrigin?: boolean;
  svgIdPrefix?: string | null;
  legacyInteractiveSelection?: boolean;
};

export const PreviewPresentation: FC<PreviewPresentationProps> = ({
  layoutElements,
  mappedElements,
  animationOverrides,
  selectedElementId,
  isIndexPreview,
  forceTopLeftOrigin,
  svgIdPrefix,
  legacyInteractiveSelection = true,
}) => {
  const mappedElementsWithoutSvg = useMemo(
    () => mappedElements.filter((element) => element.type != 'SVG'),
    [mappedElements],
  );

  const svgElement = useMemo(() => layoutElements.find((x) => x.type === 'SVG'), [layoutElements]);

  const {
    width: boxWidth,
    height: boxHeight,
    scale: transformScale,
  } = useMemo(() => {
    if (svgElement?.size.width && svgElement?.size.height) {
      return getPreviewScaledSize(svgElement.size.width, svgElement.size.height);
    }
    return { width: PREVIEW_MAX_SIZE, height: PREVIEW_MAX_SIZE, scale: 1 };
  }, [svgElement]);

  const { width: contentWidth, height: contentHeight } = useMemo(() => {
    if (svgElement?.size.width && svgElement?.size.height) {
      return {
        width: svgElement.size.width,
        height: svgElement.size.height,
      };
    }

    return {
      width: boxWidth,
      height: boxHeight,
    };
  }, [boxHeight, boxWidth, svgElement]);

  let transformOrigin =
    isIndexPreview && svgElement?.size.width === 1200 && svgElement?.size.height === 1200
      ? 'top left'
      : isIndexPreview
      ? 'top'
      : 'top left';

  if (forceTopLeftOrigin) {
    transformOrigin = 'top left';
  }

  const scaledOffsetLeft = transformOrigin === 'top' ? -((contentWidth - boxWidth) / 2) : 0;

  return (
    <Box
      position="relative"
      style={{
        width: `${boxWidth}px`,
        height: `${boxHeight}px`,
      }}
      data-testid="preview-frame"
    >
      <Box
        position="absolute"
        style={{
          top: '0px',
          left: `${scaledOffsetLeft}px`,
          width: `${contentWidth}px`,
          height: `${contentHeight}px`,
          transform: `scale(${transformScale})`,
          transformOrigin: transformOrigin,
        }}
        data-testid="preview-scaled-content"
      >
        <Ad
          adElements={mappedElements}
          isPreview={true}
          svgIdPrefix={svgIdPrefix}
          animationOverrides={animationOverrides}
        />
        {isIndexPreview ? (
          <></>
        ) : (
          <>
            <PreviewOverlay adElements={mappedElementsWithoutSvg} selectedId={selectedElementId} />
            {legacyInteractiveSelection ? (
              <SelectOverlay adElements={mappedElementsWithoutSvg} />
            ) : null}
          </>
        )}
      </Box>
    </Box>
  );
};

PreviewPresentation.displayName = 'PreviewPresentation';
